import { DateRange, DateRangeValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers-pro';
import { endOfDay, startOfDay } from 'date-fns';
import { any, isNil, isNotNil } from 'ramda';
import { useEffect } from 'react';
import { useRange } from 'react-instantsearch';

import { useDateRange } from '~/components/DateRangeProvider';

import { SearchDateRangePickerProps } from './SearchDateRangePicker';
import {
  convertToDate,
  enforceInstantSearchDateRange,
  getDateRange,
  isEmptyDateRange,
} from './useSearchDateRange.utils';

export const useSearchDateRange = ({ attribute }: SearchDateRangePickerProps) => {
  const {
    start: [startMin, startMax],
    refine,
    range,
  } = useRange({
    attribute,
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
  });

  const { currentDateRange, setCurrentDateRange, shortcuts } = useDateRange();

  useEffect(() => {
    if (isNil(startMin) || isNil(startMax)) {
      return;
    }
    if (startMin === Number.NEGATIVE_INFINITY && startMax === Number.POSITIVE_INFINITY) {
      setCurrentDateRange([null, null]);
      return;
    }
    setCurrentDateRange(getDateRange(startMin, startMax));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMin, startMax]);

  const resetDatePicker = () => {
    setCurrentDateRange([null, null]);
    refine([undefined, undefined]);
  };

  const handleChange = (dateValues: DateRange<Date>, context: PickerChangeHandlerContext<DateRangeValidationError>) => {
    if (isEmptyDateRange(dateValues)) {
      resetDatePicker();
      return;
    }

    const { validationError } = context;
    if (any(isNotNil, validationError)) {
      return;
    }

    // Skip anything if input is incomplete - both fields must be filled in
    const [min, max] = dateValues;
    if (isNil(min) || isNil(max)) {
      return;
    }

    const enforcedTimestamps = enforceInstantSearchDateRange(range, [min, max]);

    refine(enforcedTimestamps);
  };

  const min = convertToDate(range.min);
  const max = convertToDate(range.max);

  return {
    min: min ? startOfDay(min) : undefined,
    max: max ? endOfDay(max) : undefined,
    currentDateRange,
    shortcuts,
    handleChange,
  };
};
